import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';
import { usePermissions } from '@/common/hooks/usePermissions';
import { usersService, BirthdayUser } from '@/features/users/services/users.service';

export const Route = createFileRoute('/_authenticated/users-birthdays')({
  component: UsersBirthdaysPage,
});

function UsersBirthdaysPage() {
  const { hasPermission } = usePermissions();
  const [searchTerm, setSearchTerm] = useState('');
  const [includeWithoutDate, setIncludeWithoutDate] = useState(false);

  const canManage = hasPermission('users.manage');

  // Buscar aniversários
  const { data: birthdaysData, isLoading } = useQuery({
    queryKey: ['users-birthdays', includeWithoutDate],
    queryFn: async () => {
      const response = await usersService.getBirthdays(includeWithoutDate);
      return Array.isArray(response.data) ? response.data : [];
    },
    enabled: canManage,
  });

  const birthdays = useMemo<BirthdayUser[]>(() => birthdaysData || [], [birthdaysData]);

  // Filtrar por termo de busca (client-side)
  const filteredBirthdays = useMemo(() => {
    if (!searchTerm.trim()) {
      return birthdays;
    }

    const term = searchTerm.toLowerCase();
    return birthdays.filter(
      (user) =>
        user.fullName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term),
    );
  }, [birthdays, searchTerm]);

  // Função para obter badge de status do aniversário
  const getBirthdayBadge = (daysUntilBirthday: number | null, birthDate: string | null) => {
    if (!birthDate || daysUntilBirthday === null) {
      return <Badge variant="secondary">Sem data</Badge>;
    }

    if (daysUntilBirthday === 0) {
      return <Badge variant="default" className="bg-green-600">Hoje!</Badge>;
    }

    if (daysUntilBirthday <= 7) {
      return <Badge variant="default" className="bg-blue-600">Esta semana</Badge>;
    }

    if (daysUntilBirthday <= 30) {
      return <Badge variant="outline" className="border-blue-500 text-blue-700">Este mês</Badge>;
    }

    return <Badge variant="outline">Próximos meses</Badge>;
  };

  // Função para formatar data diretamente da string YYYY-MM-DD para dd/MM/yyyy
  const formatDateString = (dateString: string): string => {
    if (!dateString) return '';
    
    // Se for ISO string (com T e Z), extrair apenas a parte da data ANTES do T
    let datePart = dateString;
    if (dateString.includes('T')) {
      datePart = dateString.split('T')[0];
    }
    
    // Extrair usando regex para garantir formato YYYY-MM-DD
    const dateMatch = datePart.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!dateMatch) {
      // Se não conseguir extrair, retornar como está
      return dateString;
    }
    
    // Extrair ano, mês e dia diretamente da string (sem conversão que pode causar problemas)
    const year = dateMatch[1];
    const month = dateMatch[2];
    const day = dateMatch[3];
    
    // Retornar no formato dd/MM/yyyy
    return `${day}/${month}/${year}`;
  };

  // Função para formatar data de nascimento (formato simples: dd/MM/yyyy)
  const formatBirthDate = (date: string | null) => {
    if (!date) return 'Não informado';
    try {
      return formatDateString(date);
    } catch {
      return date;
    }
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Aniversários da Equipe</h1>
          <p className="text-muted-foreground">Você não tem permissão para acessar esta página</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Aniversários da Equipe</h1>
        <p className="text-muted-foreground">
          Visualize os aniversários dos membros da equipe organizados por data
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Aniversários</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Barra de busca e filtros */}
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="includeWithoutDate"
                checked={includeWithoutDate}
                onCheckedChange={(checked) => setIncludeWithoutDate(checked === true)}
              />
              <Label
                htmlFor="includeWithoutDate"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
              >
                Mostrar usuários sem data de aniversário
              </Label>
            </div>
          </div>

          {isLoading ? (
            <div className="py-8 text-center text-muted-foreground">Carregando...</div>
          ) : !filteredBirthdays || filteredBirthdays.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">
              {searchTerm
                ? 'Nenhum usuário encontrado com o termo de busca'
                : 'Nenhum aniversário encontrado'}
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Data de Nascimento</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredBirthdays.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">{user.fullName}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{formatBirthDate(user.birthDate)}</TableCell>
                      <TableCell>
                        {getBirthdayBadge(user.daysUntilBirthday, user.birthDate)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

